import {
  Document,
  GenerationSection,
  GenerationState,
} from "../../types/documentTypes";

type GenerationLifecycleDocument = Pick<Document, "DocumentID" | "Status">;

interface SourceUploadAccess {
  documentStatus?: Document["Status"];
  isGenerationActive: boolean;
  hasExistingSections: boolean;
  isGenerating: boolean;
  isStartingGeneration: boolean;
  isRegeneratingSection: boolean;
}

interface GenerationActionAccess {
  isSaving: boolean;
  isGenerationActive: boolean;
  isGenerating: boolean;
  isStartingGeneration: boolean;
  isRegeneratingSection: boolean;
  hasActiveSourceUploads: boolean;
  hasActiveSourceDeletions: boolean;
  hasIndexingIssue: boolean;
}

interface SectionRegenerationAccess {
  isSavingDocument: boolean;
  hasActiveSourceUploads: boolean;
  hasActiveSourceDeletions: boolean;
}

/**
 * Source uploads are allowlisted to an untouched document or a document whose
 * generation has reached a terminal status. Unknown and in-flight states are
 * denied by default.
 */
export function canUploadSourceDocuments({
  documentStatus,
  isGenerationActive,
  hasExistingSections,
  isGenerating,
  isStartingGeneration,
  isRegeneratingSection,
}: SourceUploadAccess): boolean {
  if (
    isGenerationActive ||
    isGenerating ||
    isStartingGeneration ||
    isRegeneratingSection
  ) {
    return false;
  }

  const isUntouchedDocument =
    documentStatus === "Started" && !hasExistingSections;
  const hasTerminalGeneration =
    documentStatus === "Generated" ||
    documentStatus === "Completed" ||
    documentStatus === "Failed";

  return isUntouchedDocument || hasTerminalGeneration;
}

export function shouldDisableGenerationActions({
  isSaving,
  isGenerationActive,
  isGenerating,
  isStartingGeneration,
  isRegeneratingSection,
  hasActiveSourceUploads,
  hasActiveSourceDeletions,
  hasIndexingIssue,
}: GenerationActionAccess): boolean {
  return (
    isSaving ||
    isGenerationActive ||
    isGenerating ||
    isStartingGeneration ||
    isRegeneratingSection ||
    hasActiveSourceUploads ||
    hasActiveSourceDeletions ||
    hasIndexingIssue
  );
}

/**
 * Single-section regeneration rewrites document content, so it must not run
 * while the config panel is mutating the same document. Returns the reason the
 * action is blocked, or an empty string when it is safe to regenerate.
 *
 * The mirror of `canUploadSourceDocuments`, which blocks source mutations while
 * a regeneration is in flight.
 */
export function getSectionRegenerationBlockedReason({
  isSavingDocument,
  hasActiveSourceUploads,
  hasActiveSourceDeletions,
}: SectionRegenerationAccess): string {
  if (isSavingDocument) {
    return "Wait for document changes to finish saving";
  }
  if (hasActiveSourceUploads) {
    return "Wait for all source documents to finish uploading";
  }
  if (hasActiveSourceDeletions) {
    return "Wait for source document deletion to finish";
  }
  return "";
}

/**
 * Identify the part of a document that can change generation lifecycle state.
 *
 * Source uploads replace the React document object to update metadata such as
 * SourceDocumentCount. Those metadata-only updates must not retrigger
 * generation reconciliation.
 */
export function getGenerationLifecycleKey(
  document: GenerationLifecycleDocument | null,
): string | null {
  if (!document) {
    return null;
  }

  return `${document.DocumentID}:${document.Status}`;
}

/**
 * Promote only the document whose generation stream emitted the terminal
 * completion event. A late event from a document that has since been
 * navigated away from must not mutate the newly selected document.
 */
export function applyTerminalGenerationCompletion(
  document: Document | null,
  completedDocumentId: number,
): Document | null {
  if (
    document?.DocumentID !== completedDocumentId ||
    document.Status !== "Started"
  ) {
    return document;
  }

  return {
    ...document,
    Status: "Generated",
    GenerationActive: false,
  };
}

/**
 * A source deletion refreshes the persisted section list, but is not a
 * generation lifecycle event. Preserve all completion/streaming flags.
 */
export function replaceSectionsAfterSourceDeletion(
  state: GenerationState,
  completedSections: GenerationSection[],
): GenerationState {
  return {
    ...state,
    completedSections,
  };
}
